import { Meal } from 'fitbeat.models';
import { IMealRepository } from '../interfaces/imeal-repository';

export class InMemoryMealRepository implements IMealRepository {
    private meals: Meal[] = [];

    public setAllMeals(meals: Meal[]) {
        this.meals = meals;
    }

    public async add(meal: Meal): Promise<Meal> {
        return new Promise<Meal>((resolve, reject) => {
            const mealEntry = Object.assign(new Meal(), meal);
            this.meals.push(mealEntry);
            resolve(mealEntry);
        });
    }

    public async get(uid: string): Promise<Meal | undefined> {
        return new Promise<Meal | undefined>((resolve, reject) => {
            resolve(this.meals.find((a: Meal) => a._id === uid));
        });
    }

    public update(meal: Meal): Promise<Meal> {
        return new Promise<Meal>((resolve, reject) => {
            const index = this.meals.findIndex((a: Meal) => a._id === meal._id);
            if (index < 0) {
                this.add(meal).catch((e) => { /* no-op */ });
            }
            this.meals[index] = meal;
            resolve(this.meals[index]);
        });
    }

    public getAll(): Promise<Meal[]> {
        return new Promise((resolve) => {
            resolve(this.meals);
        });
    }

    public async getImage(uid: string): Promise<string> {
        const meal: Meal | undefined = this.meals.find((a: Meal) => a._id === uid);
        if (meal) {
            return meal.image;
        }
        return '';
    }

    public async setImage(uid: string, base64Image: string): Promise<boolean> {
        const index = this.meals.findIndex((a: Meal) => a._id === uid);
        if (index > 0) {
            this.meals[index].image = base64Image;
            return true;
        }
        return false;
    }

    public async getThumbnail(uid: string): Promise<string> {
        const meal: Meal | undefined = this.meals.find((a: Meal) => a._id === uid);
        if (meal) {
            return meal.thumbnail;
        }
        return '';
    }

    public async setThumbnail(uid: string, base64Image: string): Promise<boolean> {
        const index = this.meals.findIndex((a: Meal) => a._id === uid);
        if (index > 0) {
            this.meals[index].thumbnail = base64Image;
            return true;
        }
        return false;
    }

    public async getMealsForMealPlan(mealPlanId: string): Promise<Meal[]> {
        const meals = this.meals.filter((meal) => meal.mealPlanId === mealPlanId);
        return meals;
    }

    public async getThumbnails(mealPlanId: string): Promise<string[]> {
        const meals = this.meals.reduce((thumbnails: string[], meal: Meal) => {
            if (meal.mealPlanId === mealPlanId) {
                thumbnails.push(meal.thumbnail);
            }
            return thumbnails;
        }, []);
        return meals;
    }

    public async getAllMealsForTimeOfDay(
        timeOfDay: string,
        gender: string,
        dietGoal: string,
        foodPreference: string,
        organisation?: string): Promise<Meal[]> {
        return this.meals.filter((meal) => {
          if (
            meal.timeOfMeal &&
            meal.gender &&
            meal.dietGoal &&
            meal.foodPreference
          ) {
            const timeOfMealEqual =
              meal.timeOfMeal.findIndex((time: string) => {
                return time === timeOfDay;
              }) !== -1;
            const foodPreferenceEqual =
              meal.foodPreference.findIndex((preference: string) => {
                return preference === foodPreference;
              }) !== -1;
            const dietGoalEqual = meal.dietGoal === dietGoal;
            const genderEqual = meal.gender === gender;
            let shouldBeExcluded;
            let organisationEqual;
            if (organisation) {
              organisationEqual =
                !meal.organisation || meal.organisation === organisation;
              shouldBeExcluded =
                meal.excludedOrganisations?.includes(organisation);
            } else {
              organisationEqual = !meal.organisation;
            }
            return (
              timeOfMealEqual &&
              foodPreferenceEqual &&
              dietGoalEqual &&
              genderEqual &&
              organisationEqual &&
              !shouldBeExcluded
            );
          }
          return false;
        });
    }
}
